import { produce } from "immer";
import { sortBy } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { filterMaybeArray } from "@examspark/common";
import {
  Button,
  ObjectiveText,
  Text,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconSearchLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { BucketAggregation } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useModal } from "../../../../hooks/useModal";
import { useExamBuilderSearch } from "../hooks/useSearch";

import ObjectiveSelectModal from "./objectives/ObjectiveSelectModal";

const ObjectiveItem = styled.div<{
  muted?: boolean;
}>`
  display: flex;
  gap: 3rem;
  margin: 1rem 0;
  position: relative;

  ${(props) => {
    if (props.muted) {
      return `opacity: 0.3;`;
    }
  }}
`;
const ObjectiveItemQty = styled.div`
  width: 8rem;
  position: relative;

  input {
    width: 100%;
  }

  input[type="text"] {
    border: solid 1px #ddd;
    box-sizing: border-box;
    height: 3rem;
    width: 100%;
    padding: 0.5rem;
    border-radius: 0.3rem;
  }

  input,
  textarea {
    transition: border-color 0.2s;

    &:focus {
      border-color: purple;
    }
  }

  input {
    border: solid 1px #ddd;
    box-sizing: border-box;
    height: 3rem;
    width: 100%;
    padding: 0.5rem;
  }
  textarea {
    border: solid 1px #ddd;
    box-sizing: border-box;
    height: 6rem;
    width: 100%;
    padding: 0.5rem;
  }
`;

const ObjectiveItemMeta = styled.div`
  flex: 1;
  padding: 0.5rem 0;
`;
const ObjectiveItemCount = styled.div`
  width: 6rem;
  padding: 1rem 1rem;
  box-sizing: border-box;
`;
const ObjectiveItemRemove = styled.div``;

const StyledTableHeader = styled.div`
  border-bottom: solid 1px #ddd;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-end;
  font-size: 1rem;
  text-transform: uppercase;
  line-height: 1.3rem;
  color: #333;
  gap: 3rem;
`;
const StyledColumnLabel = styled.div`
  flex: 1;
`;
const StyledColumnQty = styled.div`
  width: 6rem;
  text-align: center;
`;
const StyledColumnQtyDesired = styled.div`
  width: 8rem;
`;

const Count = styled.div`
  background-color: #006699;
  color: #fff;
  border-radius: 1rem;
  text-align: center;
  padding: 0.4rem 0.5rem;
  margin: -0.3rem 0;
  font-size: 1rem;
`;

const NewActivityObjectives = (props: { objectiveAuthorityId: string }) => {
  const [state, dispatchState] = useContext(NewActivityContext);
  const search = useExamBuilderSearch();
  const [facets, setFacets] = useState<BucketAggregation[]>();
  const pickerModal = useModal();

  const fetchObjectives = async () => {
    if (!state.sections) return;
    if (!state.language) return;
    // if (!context.app?.workspaceId) return;

    const results = await search.searchItems({
      exclude: ["passages", "objectives"],
    });
    setFacets(
      filterMaybeArray(results?.aggregations?.objectiveId?.buckets ?? []),
    );
  };

  useEffect(() => {
    fetchObjectives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.section, state.language, state.interactionTypes, state.passages]);

  return (
    <>
      <ObjectiveSelectModal
        objectiveAuthorityId={props.objectiveAuthorityId}
        {...pickerModal.modalProps}
        onSubmit={(values) => {
          if (state.objectives.length + values.length > 20) {
            alert(
              "Your activity can only consist of up to 20 standards. Try removing some standards before selecting new standards.",
            );
            return;
          }
          dispatchState({
            type: "UPDATE",
            data: produce(state, (draft) => {
              for (const v of values) {
                if (!state.objectives.find((_obj) => _obj.id === v.id)) {
                  draft.objectives.push({
                    id: v.id,
                    sk: "standard",
                    type: "OBJECTIVE",
                    content: v.content || "",
                    abbreviation: v.abbreviation || "",
                    sequence: v.sequence || 0,
                    qty: "",
                  });
                }
              }
            }),
          });
          pickerModal.close();
        }}
      />
      <VSpace>
        <Text.H2>Standards to Assess</Text.H2>
        <Text.Body>
          Select up to 20 standards you would like to assess in your activity.
        </Text.Body>
        {!!state.objectives.length && (
          <div>
            <StyledTableHeader>
              <StyledColumnQtyDesired>
                Desired
                <br />
                Qty
              </StyledColumnQtyDesired>
              <StyledColumnQty>
                Available
                <br />
                Qty
              </StyledColumnQty>
              <StyledColumnLabel>Standard</StyledColumnLabel>
            </StyledTableHeader>
            {sortBy(state.objectives, ["sequence"]).map((obj, idx) => {
              const count = facets?.find((f) => f.key === obj.id);
              return (
                <ObjectiveItem key={obj.id} muted={!count?.doc_count}>
                  <ObjectiveItemQty>
                    <TextInput
                      label="Qty:"
                      maxLength={2}
                      onChange={(e) => {
                        const _index = state.objectives.findIndex(
                          (_obj) => _obj.id === obj.id,
                        );
                        if (_index === -1) return;
                        dispatchState({
                          type: "UPDATE",
                          data: produce(state, (draft) => {
                            draft.objectives[_index].qty = e.target.value;
                          }),
                        });
                      }}
                      selectOnFocus
                      textAlignment="right"
                      value={obj.qty || ""}
                    />
                  </ObjectiveItemQty>
                  <ObjectiveItemCount>
                    <Count>{count?.doc_count || "0"}</Count>
                  </ObjectiveItemCount>
                  <ObjectiveItemMeta>
                    <ObjectiveText
                      objective={{
                        id: obj.id,
                        sk: "standard",
                        type: "standard",
                        content: obj.content,
                        abbreviation: obj.abbreviation,
                      }}
                    />
                  </ObjectiveItemMeta>

                  <ObjectiveItemRemove>
                    <Button
                      title="Remove Standard"
                      icon={<IconTrashLine width="16" />}
                      variant="outlined"
                      color="danger"
                      onClick={() => {
                        const _index = state.objectives.findIndex(
                          (_obj) => _obj.id === obj.id,
                        );
                        if (_index === -1) return;
                        dispatchState({
                          type: "UPDATE",
                          data: produce(state, (draft) => {
                            draft.objectives.splice(_index, 1);
                          }),
                        });
                      }}
                    />
                  </ObjectiveItemRemove>
                </ObjectiveItem>
              );
            })}
          </div>
        )}
        <Toolbar>
          <Button
            title="Browse Standards"
            icon={<IconSearchLine />}
            variant="outlined"
            color="primary"
            onClick={() => {
              pickerModal.open();
            }}
          >
            Select Standards
          </Button>
          {/* <Button
            title="Refresh"
            variant="outlined"
            onClick={() => {
              fetchObjectives();
            }}
          >
            Refresh
          </Button> */}
        </Toolbar>
      </VSpace>
    </>
  );
};

export default NewActivityObjectives;
